import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const accountOptions = {
  artist: {
    title: 'Reset Artist Password',
    requestNote: 'Use the same email address you use for KalaManch on the website and in the Android app.',
    resetNote: 'Choose a new KalaManch password. Use at least 8 characters.',
    backHref: '/',
    backLabel: 'Back to KalaManch',
  },
  collaborator: {
    title: 'Reset Collaborator Password',
    requestNote: 'Use the same email address you use for KalaManch on the website and in the Android app.',
    resetNote: 'Choose a new KalaManch password. Use at least 8 characters.',
    backHref: '/collaborators',
    backLabel: 'Back to Collaborators',
  },
};

export default function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const accountType = searchParams.get('account') === 'collaborator' ? 'collaborator' : 'artist';
  const initialEmail = searchParams.get('email') || '';
  const resetToken = searchParams.get('token') || '';
  const isResetMode = Boolean(resetToken);
  const copy = useMemo(() => accountOptions[accountType], [accountType]);

  const [email, setEmail] = useState(initialEmail);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  const handleRequestSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setStatus('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/request-password-reset`, {
        role: accountType,
        email,
      });
      setStatus(response.data?.message || 'Password reset email sent. Please check inbox and spam.');
    } catch (submitError) {
      setError(submitError.response?.data?.detail || submitError.message || 'Unable to send password reset email.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setStatus('');

    try {
      if (newPassword.trim().length < 8) {
        throw new Error('New password must be at least 8 characters.');
      }
      if (newPassword !== confirmPassword) {
        throw new Error('New password and confirm password must match.');
      }

      const response = await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword,
      });
      setStatus(response.data?.message || 'Password changed successfully. A confirmation email has been sent.');
      setNewPassword('');
      setConfirmPassword('');
    } catch (submitError) {
      setError(submitError.response?.data?.detail || submitError.message || 'Unable to reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-[#FFF7F2] px-4 py-14 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-xl rounded-[28px] border border-[#FFD9BC] bg-white p-6 shadow-[0_18px_60px_rgba(30,41,59,0.12)] sm:p-8">
        <p className="text-sm font-semibold uppercase tracking-[0.28em] text-[#FF6F00]">Account Help</p>
        <h1 className="mt-3 text-3xl font-bold text-slate-900 sm:text-4xl">{copy.title}</h1>
        <p className="mt-4 text-base leading-7 text-slate-600">{isResetMode ? copy.resetNote : copy.requestNote}</p>

        <form onSubmit={isResetMode ? handleResetSubmit : handleRequestSubmit} className="mt-8 space-y-4">
          {!isResetMode ? (
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-slate-700">Email address</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-base text-slate-900 outline-none transition focus:border-[#FF6F00] focus:ring-2 focus:ring-[#FFD9BC]"
                autoComplete="email"
                required
              />
            </label>
          ) : (
            <>
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-700">New password</span>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  placeholder="New password"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-base text-slate-900 outline-none transition focus:border-[#FF6F00] focus:ring-2 focus:ring-[#FFD9BC]"
                  autoComplete="new-password"
                  required
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-700">Confirm new password</span>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  placeholder="Confirm new password"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-base text-slate-900 outline-none transition focus:border-[#FF6F00] focus:ring-2 focus:ring-[#FFD9BC]"
                  autoComplete="new-password"
                  required
                />
              </label>
            </>
          )}

          {error ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          ) : null}

          {status ? (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {status}
            </div>
          ) : null}

          <button type="submit" disabled={loading} className="btn-primary w-full">
            {loading
              ? isResetMode
                ? 'Resetting password...'
                : 'Sending reset email...'
              : isResetMode
                ? 'Set New Password'
                : 'Send Reset Email'}
          </button>
        </form>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 text-sm">
          <Link to={copy.backHref} className="font-semibold text-[#FF6F00] hover:text-[#E66300]">
            {copy.backLabel}
          </Link>
          <span className="text-slate-500">
            {isResetMode
              ? 'After reset, use the new KalaManch password on the website.'
              : 'Use the same KalaManch email address you sign in with.'}
          </span>
        </div>
      </div>
    </section>
  );
}
